import { format } from 'date-fns'
import { enUS, ru } from 'date-fns/locale'
import type { IsoLanguage } from 'src/types/language'
import filterMonthPred from 'common/filters/filterMonthPred'
import tStatic from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_functions/tStatic'
import parseReviewDate from 'src/components/pages/[town-translit]/lpu/[lpu-translit]/_components/LpuReviews/functions/parseReviewDate'

function getDateLocale(languageIso?: IsoLanguage) {
  return languageIso === 'ru' ? ru : enUS
}

/**
 * Дата публикации отзыва в карточке.
 *
 * Год текущего года скрывается, чтобы свежие отзывы не выглядели «архивными».
 * Время всегда с префиксом «в»: иначе `12 марта 14:30` читается как одно число.
 */
export function formatPublishedDate(
  value: string | Date | null | undefined,
  languageIso?: IsoLanguage,
): string {
  const date = parseReviewDate(value)

  if (!date) {
    return ''
  }

  const locale = getDateLocale(languageIso)
  const isCurrentYear = date.getFullYear() === new Date().getFullYear()
  const datePart = format(date, isCurrentYear ? 'd MMMM' : 'd MMMM yyyy', { locale })

  return `${datePart} ${tStatic('в')} ${format(date, 'HH:mm')}`
}

/**
 * Дата публикации для `itemprop="datePublished"`.
 *
 * В карточке год текущего года скрыт, а микроразметке нужна полная ISO-дата.
 */
export function formatPublishedIsoDate(value: string | Date | null | undefined): string {
  const date = parseReviewDate(value)

  if (!date) {
    return ''
  }

  return format(date, 'yyyy-MM-dd')
}

/**
 * Дата ответа клиники или модератора.
 *
 * Год всегда на месте, в отличие от даты публикации:
 * ответ может быть через месяцы, и без года неочевидно, когда он дан.
 */
export function formatReplyDate(
  value: string | Date | null | undefined,
  languageIso?: IsoLanguage,
): string {
  const date = parseReviewDate(value)

  if (!date) {
    return ''
  }

  return `${format(date, 'dd MMMM yyyy', { locale: getDateLocale(languageIso) })} ${tStatic('в')} ${format(date, 'HH:mm')}`
}

/**
 * Компактная дата удаления внутри длинного служебного алерта.
 */
export function formatDeletedDate(value: string | Date | null | undefined): string {
  const date = parseReviewDate(value)

  if (!date) {
    return ''
  }

  return format(date, 'dd.MM.yyyy')
}

/**
 * Дата дополнения пациента в заголовке «Дополнение от …».
 *
 * Только календарный день: время правки в карточке не показывается.
 */
export function formatSupplementDate(
  value: string | Date | null | undefined,
  languageIso?: IsoLanguage,
): string {
  const date = parseReviewDate(value)

  if (!date) {
    return ''
  }

  return format(date, 'd MMMM yyyy', { locale: getDateLocale(languageIso) })
}

/**
 * Месяц визита в предложном падеже для русской локали (`в марте 2024`).
 *
 * Для остальных языков — именительный месяц из date-fns:
 * `filterMonthPred` знает только русские формы.
 */
export function formatVisitMonth(
  value: string | Date | null | undefined,
  isRussia: boolean,
  languageIso?: IsoLanguage,
): string {
  const date = parseReviewDate(value)

  if (!date) {
    return ''
  }

  const monthLabel = isRussia
    ? filterMonthPred(date.getMonth() + 1)
    : format(date, 'LLLL', { locale: getDateLocale(languageIso) })

  return `${monthLabel} ${date.getFullYear()}`
}

/**
 * Год публикации для `data-year` и класса `yearYYYY` на карточке.
 *
 * В видимой дате год текущего года опускается,
 * поэтому разметке нужен отдельный источник.
 */
export function getReviewYear(value: string | Date | null | undefined): string {
  const date = parseReviewDate(value)

  return date ? String(date.getFullYear()) : ''
}
